import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { streamFlowApi } from '@lib/streamFlowApi';
import { HealthDot, ReliabilityPill, ago } from '@components/adminUi';

/**
 * Per-channel reliability board (Phase 2 observability). Ranks channels
 * worst-first by a real success rate computed from playback telemetry
 * (sf_playback_events) + source health (sf_channel_source_health), so the
 * operator sees "flaky for days" vs "blipped once" and can Probe on the spot.
 * Server gates on isAdminEmail. Auto-refreshes every 30s.
 */
type HealthData = NonNullable<Awaited<ReturnType<typeof streamFlowApi.adminChannelHealth>>['data']>;
type ProbeResult = NonNullable<Awaited<ReturnType<typeof streamFlowApi.adminProbeChannel>>['data']>;

export default function AdminChannelHealth() {
  const navigate = useNavigate();
  const [data, setData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [probing, setProbing] = useState<string | null>(null);
  const [probes, setProbes] = useState<Record<string, ProbeResult>>({});

  const refresh = useCallback(async () => {
    const res = await streamFlowApi.adminChannelHealth();
    if (res.success && res.data) setData(res.data);
    else if (res.error) toast.error(res.error.message);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), 30_000);
    return () => clearInterval(t);
  }, [refresh]);

  const probe = async (channelId: string) => {
    setProbing(channelId);
    const res = await streamFlowApi.adminProbeChannel(channelId);
    if (res.success && res.data) setProbes((p) => ({ ...p, [channelId]: res.data! }));
    else toast.error(res.error?.message ?? 'Probe failed');
    setProbing(null);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Channel health</h1>
          <p className="text-sm text-gray-500">Real reliability from playback telemetry, worst-first. Auto-refreshes every 30s.</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => void refresh()} className="text-sm text-blue-600 hover:underline">Refresh</button>
          <button onClick={() => navigate('/admin/status')} className="text-sm text-blue-600 hover:underline">Status ▸</button>
          <button onClick={() => navigate('/')} className="text-sm text-blue-600 hover:underline">← Dashboard</button>
        </div>
      </div>

      {loading && !data && <div className="text-sm text-gray-500">Loading…</div>}
      {data && data.channels.length === 0 && <div className="text-sm text-gray-500">No telemetry yet — channels appear here once TVs report playback events.</div>}

      {data && data.channels.length > 0 && (
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Channel</th>
                <th className="px-3 py-2 text-left">24h</th>
                <th className="px-3 py-2 text-left">7d</th>
                <th className="px-3 py-2 text-left">24h issues</th>
                <th className="px-3 py-2 text-left">Mirrors</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {data.channels.map((c) => (
                <tr key={c.channel_id} className="border-t border-gray-100 align-top">
                  <td className="px-3 py-2 font-medium">{c.name}</td>
                  <td className="px-3 py-2"><ReliabilityPill score={c.reliability_24h} /></td>
                  <td className="px-3 py-2"><ReliabilityPill score={c.reliability_7d} /></td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {c.fatal_24h > 0 && <span className="text-rose-600">{c.fatal_24h} fatal </span>}
                    {c.failover_24h > 0 && <span className="text-amber-600">{c.failover_24h} failover </span>}
                    {c.rebuffer_24h > 0 && <span>{c.rebuffer_24h} rebuffer</span>}
                    {c.fatal_24h + c.failover_24h + c.rebuffer_24h === 0 && <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="space-y-0.5">
                      {c.sources.map((s) => (
                        <div key={s.provider_id} className="flex items-center gap-1.5 text-xs">
                          <HealthDot status={s.health_status} />
                          <span>{s.provider_name}</span>
                          {s.consecutive_failures > 0 && <span className="text-rose-500">({s.consecutive_failures}✗ {ago(s.last_failure_at)})</span>}
                        </div>
                      ))}
                    </div>
                    {probes[c.channel_id] && (
                      <div className="mt-1 text-[11px] text-gray-500 border-l-2 border-gray-200 pl-2">
                        {probes[c.channel_id].mirrors.map((m) => (
                          <div key={m.provider_id} className="flex items-center gap-1">
                            <HealthDot status={m.alive ? 'healthy' : 'unhealthy'} />
                            <span>{m.provider_name}</span><span className="text-gray-400">{m.egress} · {m.probe_status ?? 'ERR'}</span>
                          </div>
                        ))}
                        <div className={probes[c.channel_id].any_alive ? 'text-emerald-600' : 'text-rose-600'}>
                          {probes[c.channel_id].any_alive ? 'at least one mirror alive' : 'all mirrors dead (likely dead upstream)'}
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button disabled={probing === c.channel_id} onClick={() => void probe(c.channel_id)}
                      className="text-xs text-blue-600 hover:underline disabled:opacity-50">
                      {probing === c.channel_id ? 'Probing…' : 'Probe'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
